import React, { useMemo, useCallback, useState } from 'react';
import { View, StyleSheet, Platform, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useModalStore } from '@/store';
import { useRouter } from 'expo-router';
import { Image } from 'expo-image';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import useResponsiveness from '@/hooks/useResponsiveness';
import { trackEvent } from '@/constants/utils/analytics';
import { useTranslation } from '@/hooks/useTranslation';
import { canOfferPremiumUpgrade } from '@/lib/purchaseUtils';
import { formatCreditBalance } from '@/lib/formatCredits';
import { availableCredits } from '@/lib/availableCredits';
import { resolveAccountButtonPadding } from './sidebarAccountButtonPadding';
import MacUpdateButton from './MacUpdateButton';
import BusinessWorkspaceMenu from '@/components/Business/BusinessWorkspaceMenu';
import { useBusinessWorkspace, workspacePath } from '@/components/Business/BusinessWorkspaceContext';

const isNativeMobile = Platform.OS === 'ios' || Platform.OS === 'android';

const createStyles = (colors, isXS, bottomInset) => StyleSheet.create({
  container: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
    borderTopWidth: 1,
    borderTopColor: colors.solid_color_block,
    ...resolveAccountButtonPadding({ platform: Platform.OS, bottomInset, isXS }),
    minHeight: Platform.OS === 'macos' ? 54 : undefined,
  },
  triggerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    paddingVertical: isNativeMobile ? 8 : 4,
    paddingLeft: isNativeMobile ? 10 : Platform.OS === 'web' ? 5 : Platform.OS === 'macos' ? 10 : 0,
    borderRadius: isNativeMobile ? 8 : 6,
    paddingRight: isNativeMobile ? 10 : (
      Platform.OS === 'macos' ? 10 :
      Platform.OS === 'web' ? 5 : 0
    ),
    minHeight: isNativeMobile ? 48 : Platform.OS === 'macos' ? 32 : undefined,
  },
  avatarContainer: {
    width: isNativeMobile ? 28 : isXS ? 20 : Platform.OS === 'macos' ? 18 : 25,
    height: isNativeMobile ? 28 : isXS ? 20 : Platform.OS === 'macos' ? 18 : 25,
    borderRadius: 16,
    backgroundColor: colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
  },
  avatarInitial: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text_color_primary,
  },
  textContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  ellipsisIcon: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopAccountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    width: '100%',
  },
  desktopIdentityRow: {
    flex: 1,
    minWidth: 0,
  },
  desktopIconButton: {
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const HoverableTriggerRow = React.forwardRef(function HoverableTriggerRow({
  children,
  style,
  colors,
  compact = false,
  accessibilityLabel,
  onMouseEnter,
  onMouseLeave,
  onHoverIn,
  onHoverOut,
  ...triggerProps
}, ref) {
  const [hovered, setHovered] = useState(false);
  const TriggerRow = Platform.OS === 'web' ? View : Pressable;
  return (
    <TriggerRow
      ref={ref}
      {...triggerProps}
      onMouseEnter={Platform.OS === 'web' ? (event) => {
        onMouseEnter?.(event);
        setHovered(true);
      } : onMouseEnter}
      onMouseLeave={Platform.OS === 'web' ? (event) => {
        onMouseLeave?.(event);
        setHovered(false);
      } : onMouseLeave}
      onHoverIn={Platform.OS !== 'web' ? (event) => {
        onHoverIn?.(event);
        setHovered(true);
      } : onHoverIn}
      onHoverOut={Platform.OS !== 'web' ? (event) => {
        onHoverOut?.(event);
        setHovered(false);
      } : onHoverOut}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={StyleSheet.flatten([style, hovered && { backgroundColor: compact ? colors.solid_color_secondary : colors.solid_color_tertiary }])}
    >
      {children}
    </TriggerRow>
  );
});

const HoverableIconButton = React.forwardRef(function HoverableIconButton({
  children,
  style,
  colors,
  onMouseEnter,
  onMouseLeave,
  onPress,
  accessibilityLabel,
  ...buttonProps
}, ref) {
  const [hovered, setHovered] = useState(false);
  return (
    <Pressable
      ref={ref}
      {...buttonProps}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      onPress={onPress}
      onMouseEnter={Platform.OS === 'web' ? (event) => {
        onMouseEnter?.(event);
        setHovered(true);
      } : onMouseEnter}
      onMouseLeave={Platform.OS === 'web' ? (event) => {
        onMouseLeave?.(event);
        setHovered(false);
      } : onMouseLeave}
      style={StyleSheet.flatten([style, hovered && { backgroundColor: colors.solid_color_tertiary }])}
    >
      {children}
    </Pressable>
  );
});

function SidebarAccountButton({ navigation, compact = false }) {
  const businessContext = useBusinessWorkspace();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const router = useRouter();
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => createStyles(colors, isXS, insets.bottom), [colors, isXS, insets.bottom]);

  const email = useAuthStore(state => state.user?.email);
  const fullName = useAuthStore(state => state.user?.fullName);
  const avatarPath = useAuthStore(state => state.user?.avatarPath);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isPlusPremium = useAuthStore(state => state.user?.flags?.isPlusPremium);
  const companyBilling = useAuthStore(state => !!state.user?.credits?.business);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const totalAvailableCredits = useAuthStore(state => availableCredits(state.user?.credits));
  const reservedCredits = useAuthStore(state => state.user?.credits?.reservedCredits ?? 0);
  const fetchUserCredits = useAuthStore(state => state.fetchUserCredits);
  const activePurchase = useAuthStore(state => state.user?.activePurchase);
  const clearSession = useAuthStore(state => state.clearSession);
  const { openModal } = useModalStore(state => state.actions);

  const displayName = fullName || email || 'Account';
  const handle = email;

  // Get first letter for avatar initial
  const avatarInitial = useMemo(() => {
    if (fullName) return fullName.charAt(0).toUpperCase();
    if (email) return email.charAt(0).toUpperCase();
    return '?';
  }, [fullName, email]);

  const handleSettingsPress = useCallback(() => {
    if ((Platform.OS === 'web' || Platform.OS === 'macos') && !isXS) {
      openModal('SettingsModalWeb');
    } else {
      openModal('SettingsModalMobile');
    }
  }, [openModal, isXS]);

  const handleCreditsPress = useCallback(() => {
    if ((Platform.OS === 'web' || Platform.OS === 'macos') && !isXS) {
      openModal('SettingsModalWeb', { tab: 'credits' });
    } else {
      openModal('SettingsModalMobile', { initialScreen: 'credits' });
    }
  }, [openModal, isXS]);

  const handleMenuOpenChange = useCallback((open) => {
    if (open && !isGhost) fetchUserCredits();
    if (open) businessContext?.directory.refresh();
  }, [fetchUserCredits, isGhost, businessContext]);

  const creditsTitle = `${t('settingsWeb.credits')}: ${totalAvailableCredits == null ? '—' : formatCreditBalance(totalAvailableCredits)}${reservedCredits > 0 ? ` available · ${formatCreditBalance(reservedCredits)} pending` : ''}`;

  const handleUpgradePress = useCallback(() => {
    trackEvent('conversion', 'upgrade_banner_clicked', 'sidebar_account_menu');
    openModal('PricingModal');
  }, [openModal]);

  // iOS upgrades use the same Apple subscription group.
  const canUpgradeToPremium = isPlus && !isPlusPremium && !isGhost && !companyBilling && canOfferPremiumUpgrade(Platform.OS, activePurchase);

  const handleUpgradeToPremiumPress = useCallback(() => {
    trackEvent('conversion', 'premium_upgrade_clicked', 'sidebar_account_menu');
    openModal('PricingModal');
  }, [openModal]);

  const handleLogout = useCallback(async () => {
    try {
      await clearSession();
      if (Platform.OS === 'web') {
        router.replace('/');
      } else {
        router.replace('/login');
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, [clearSession, router]);

  const handlePersonalizationPress = useCallback(() => {
    if ((Platform.OS === 'web' || Platform.OS === 'macos') && !isXS) {
      openModal('SettingsModalWeb', { tab: 'profile' });
    } else {
      openModal('SettingsModalMobile', { initialScreen: 'profile' });
    }
  }, [openModal, isXS]);

  const handleHelpPress = useCallback(() => {
    if ((Platform.OS === 'web' || Platform.OS === 'macos') && !isXS) {
      openModal('SettingsModalWeb', { tab: 'help' });
    } else {
      openModal('SettingsModalMobile', { initialScreen: 'help' });
    }
  }, [openModal, isXS]);

  const handleDownloadAppsPress = useCallback(() => {
    trackEvent('conversion', 'download_apps_menu_clicked', 'sidebar_account_menu');
    openModal('DownloadAppsModal');
  }, [openModal]);

  const isWeb = Platform.OS === 'web';
  const useDetachedDesktopActions = !compact && ((Platform.OS === 'web' && !isXS) || Platform.OS === 'macos');
  const identityContent = (
    <>
      <View style={styles.avatarContainer}>
        {avatarPath ? (
          <Image
            source={{ uri: avatarPath }}
            style={styles.avatarImage}
            contentFit="cover"
          />
        ) : (
          <Text style={styles.avatarInitial}>{avatarInitial}</Text>
        )}
      </View>
      <View style={styles.textContainer}>
        <Text size="n" weight="medium" numberOfLines={1} style={isNativeMobile ? { fontSize: 17, lineHeight: 24 } : undefined}>{displayName}</Text>
        {!isGhost && (
          <Text size="xs" color="tertiary" numberOfLines={1} style={isNativeMobile ? { fontSize: 14, lineHeight: 20 } : undefined}>
            {isPlusPremium ? 'Plus Premium' : (isPlus ? 'Plus' : 'Free')}
          </Text>
        )}
      </View>
    </>
  );

  const menuContent = (
    <DropdownMenu.Content
      className={Platform.OS === 'web' ? "DropdownMenuContent" : undefined}
      align="start"
      side="top"
      sideOffset={8}
    >
      {Platform.OS === 'web' ? (
        <>
          <DropdownMenuLabel text={handle || displayName} />
          <DropdownMenuItemDivider />
          <BusinessWorkspaceMenu />
          {!isGhost && (
            <DropdownMenuItem
              key="credits"
              onSelect={handleCreditsPress}
              icon="coins"
              title={creditsTitle}
            />
          )}
          {!isPlus && !isGhost && !companyBilling && (
            <DropdownMenuItem
              key="upgrade"
              onSelect={handleUpgradePress}
              icon="sparkles"
              title={t('sidebar.upgradePlan')}
            />
          )}
          {canUpgradeToPremium && (
            <DropdownMenuItem
              key="upgrade-premium"
              onSelect={handleUpgradeToPremiumPress}
              icon="gem"
              title="Upgrade to Plus Premium"
            />
          )}
          <DropdownMenuItem
            key="personalization"
            onSelect={handlePersonalizationPress}
            icon="circle-user"
            title={t('sidebar.personalization')}
          />
          <DropdownMenuItem
            key="settings"
            onSelect={handleSettingsPress}
            icon="gear"
            title={t('sidebar.settings')}
          />
          <DropdownMenuItemDivider />
          {isWeb && (
            <DropdownMenuItem
              key="download-apps"
              onSelect={handleDownloadAppsPress}
              icon="mobile-screen-button"
              title={t('sidebar.downloadGabAiApp')}
            />
          )}
          <DropdownMenuItem
            key="help"
            onSelect={handleHelpPress}
            icon="circle-question"
            title={t('sidebar.help')}
          />
          <DropdownMenuItem
            key="logout"
            onSelect={handleLogout}
            icon="arrow-right-from-bracket"
            title={t('sidebar.logOut')}
          />
        </>
      ) : (
        <>
          <DropdownMenu.Item key="account-label" disabled>
            <DropdownMenu.ItemTitle>{handle || displayName}</DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
          <DropdownMenu.Separator />
          {!!businessContext?.workspaces.length && <>
            <DropdownMenu.Item key="workspace-personal" onSelect={() => { router.push('/app'); if (isNativeMobile) navigation?.closeDrawer(); }}>
              <DropdownMenu.ItemTitle>Personal</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
            {businessContext.workspaces.map(workspace => <DropdownMenu.Item key={`workspace-${workspace.id}`}
              onSelect={() => { router.push(workspacePath(workspace.id)); if (isNativeMobile) navigation?.closeDrawer(); }}>
              <DropdownMenu.ItemIcon ios={{ name: 'building.2' }} />
              <DropdownMenu.ItemTitle>{workspace.name}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>)}
            <DropdownMenu.Separator />
          </>}
          {!isGhost && (
            <DropdownMenu.Item key="credits" onSelect={handleCreditsPress}>
              <DropdownMenu.ItemIcon ios={{ name: 'creditcard' }} />
              <DropdownMenu.ItemTitle>{creditsTitle}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          )}
          {!isPlus && !isGhost && (
            <DropdownMenu.Item key="upgrade" onSelect={handleUpgradePress}>
              <DropdownMenu.ItemIcon ios={{ name: 'sparkles' }} />
              <DropdownMenu.ItemTitle>{t('sidebar.upgradePlan')}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item key="personalization" onSelect={handlePersonalizationPress}>
            <DropdownMenu.ItemIcon ios={{ name: 'person.circle' }} />
            <DropdownMenu.ItemTitle>{t('sidebar.personalization')}</DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
          <DropdownMenu.Item key="settings" onSelect={handleSettingsPress}>
            <DropdownMenu.ItemIcon ios={{ name: 'gearshape' }} />
            <DropdownMenu.ItemTitle>{t('sidebar.settings')}</DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
          <DropdownMenu.Separator />
          <DropdownMenu.Item key="help" onSelect={handleHelpPress}>
            <DropdownMenu.ItemIcon ios={{ name: 'questionmark.circle' }} />
            <DropdownMenu.ItemTitle>{t('sidebar.help')}</DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
          <DropdownMenu.Item key="logout" onSelect={handleLogout}>
            <DropdownMenu.ItemIcon ios={{ name: 'rectangle.portrait.and.arrow.right' }} />
            <DropdownMenu.ItemTitle>{t('sidebar.logOut')}</DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        </>
      )}
    </DropdownMenu.Content>
  );

  return (
    <View style={[styles.container, Platform.OS === 'macos' && { flexDirection: compact ? 'column' : 'row', alignItems: 'center', gap: 6 }, compact && { ...resolveAccountButtonPadding({ platform: Platform.OS, bottomInset: insets.bottom, isXS, compact: true }), alignItems: 'center' }]}>
      <View style={Platform.OS === 'macos' ? (compact ? { width: '100%', alignItems: 'center' } : { flex: 1, minWidth: 0 }) : { width: '100%', alignItems: compact ? 'center' : undefined }}>
      {useDetachedDesktopActions ? (
        <View style={styles.desktopAccountRow}>
          <View style={[styles.triggerRow, styles.desktopIdentityRow]}>
            {identityContent}
          </View>
          <HoverableIconButton
            colors={colors}
            accessibilityLabel={t('sidebar.settings')}
            onPress={handleSettingsPress}
            style={styles.desktopIconButton}
          >
            <Icon name="gear" size={16} color={colors.text_color_secondary} />
          </HoverableIconButton>
          <DropdownMenu.Root onOpenChange={handleMenuOpenChange}>
            <DropdownMenu.Trigger asChild style={styles.desktopIconButton}>
              <HoverableIconButton
                colors={colors}
                accessibilityLabel={t('sidebar.accountMenu')}
                style={styles.desktopIconButton}
              >
                <Icon name="ellipsis" size={16} color={colors.text_color_secondary} />
              </HoverableIconButton>
            </DropdownMenu.Trigger>
            {menuContent}
          </DropdownMenu.Root>
        </View>
      ) : (
      <DropdownMenu.Root onOpenChange={handleMenuOpenChange}>
        {/* Native Zeego triggers replace their child's style with this prop. */}
        <DropdownMenu.Trigger asChild style={Platform.OS === 'macos' ? { width: compact ? 44 : '100%' } : StyleSheet.flatten([styles.triggerRow, compact && { width: 44, height: 44, paddingLeft: 0, paddingRight: 0, paddingVertical: 0, justifyContent: 'center', borderRadius: 11 }])}>
          <HoverableTriggerRow
            colors={colors}
            compact={compact}
            accessibilityLabel={t('sidebar.accountMenu')}
            // Keep the native row layout while the trigger contributes its width.
            style={Platform.OS === 'macos' ? StyleSheet.flatten([styles.triggerRow, compact && { width: 44, height: 44, paddingLeft: 0, paddingRight: 0, paddingVertical: 0, justifyContent: 'center', borderRadius: 11 }]) : undefined}
          >
            <View style={[styles.avatarContainer, compact && { width: 28, height: 28 }]}>
              {avatarPath ? (
                <Image
                  source={{ uri: avatarPath }}
                  style={styles.avatarImage}
                  contentFit="cover"
                />
              ) : (
                <Text style={styles.avatarInitial}>{avatarInitial}</Text>
              )}
            </View>
            {!compact ? <View style={styles.textContainer}>
              <Text size="n" weight="medium" numberOfLines={1} style={isNativeMobile ? { fontSize: 17, lineHeight: 24 } : undefined}>{displayName}</Text>
              {!isGhost && (
                <Text size="xs" color="tertiary" numberOfLines={1} style={isNativeMobile ? { fontSize: 14, lineHeight: 20 } : undefined}>
                  {isPlusPremium ? 'Plus Premium' : (isPlus ? 'Plus' : 'Free')}
                </Text>
              )}
            </View> : null}
            {!compact ? <View style={styles.ellipsisIcon}>
              <Icon name="ellipsis" size={16} color={colors.text_color_secondary} />
            </View> : null}
          </HoverableTriggerRow>
        </DropdownMenu.Trigger>
        {menuContent}
      </DropdownMenu.Root>
      )}
      </View>
      <MacUpdateButton compact={compact} />
    </View>
  );
}

export default React.memo(SidebarAccountButton);
